// Header.js
import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { doc, onSnapshot } from 'firebase/firestore';
import { auth, db } from '../firebase'; 
import { useAuth } from '../contexts/AuthContext';
import Tooltip from "./Tooltip";
import { FaBars, FaTimes, FaAngleDown, FaVideo } from "react-icons/fa";
import '../App.css';

function Header() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [userData, setUserData] = useState(null);
  const [isMobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    if (!user) {
      setUserData(null);
      return;
    }
    const docRef = doc(db, 'users', user.uid);
    const unsubscribe = onSnapshot(docRef, (snapshot) => {
      if (snapshot.exists()) {
        setUserData(snapshot.data());
      }
    });
    return () => unsubscribe();
  }, [user]);

  const handleBuyCredits = () => {
    navigate('/buy-credits');
    setMobileMenuOpen(false);
  };

  const handleSignOut = async () => {
    try {
      // Clear guest tokens on logout
      localStorage.removeItem('guestShareGroupId');
      localStorage.removeItem('guestShareGroupToken');
      setStoredShareGroupId(null);
      setStoredShareGroupToken(null);
      
      await logout();
      navigate('/login');
    } catch (err) {
      console.error('Sign out error:', err);
    }
  };

  const handleSignIn = () => {
    navigate('/login');
  };

  const guestShareGroupId = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get('id');
  }, [location.search]);

  const shareGroupToken = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get('shareGroupToken');
  }, [location.search]);

  const [storedShareGroupId, setStoredShareGroupId] = useState(null);
  const [storedShareGroupToken, setStoredShareGroupToken] = useState(null);

  useEffect(() => {
    if (guestShareGroupId) {
      localStorage.setItem('guestShareGroupId', guestShareGroupId);
      setStoredShareGroupId(guestShareGroupId);
      return;
    }
    const cached = localStorage.getItem('guestShareGroupId');
    if (cached) {
      setStoredShareGroupId(cached);
    }
  }, [guestShareGroupId]);

  useEffect(() => {
    if (shareGroupToken) {
      localStorage.setItem('guestShareGroupToken', shareGroupToken);
      setStoredShareGroupToken(shareGroupToken);
      return;
    }
    const cached = localStorage.getItem('guestShareGroupToken');
    if (cached) {
      setStoredShareGroupToken(cached);
    }
  }, [shareGroupToken]);

  // Clear guest tokens when a real (non-anonymous) user signs in
  useEffect(() => {
    if (user && !user.isAnonymous) {
      // Real user signed in - clear guest tokens
      localStorage.removeItem('guestShareGroupId');
      localStorage.removeItem('guestShareGroupToken');
      setStoredShareGroupId(null);
      setStoredShareGroupToken(null);
    }
  }, [user]);

  const effectiveShareGroupId = guestShareGroupId || storedShareGroupId;
  const effectiveShareGroupToken = shareGroupToken || storedShareGroupToken;
  const isGuest = Boolean(user?.isAnonymous);
  const showGuestNav = Boolean(effectiveShareGroupId) || Boolean(effectiveShareGroupToken) || isGuest;

  const buildGuestPath = (path) => {
    if (effectiveShareGroupToken) {
      return `${path}?shareGroupToken=${effectiveShareGroupToken}`;
    }
    if (effectiveShareGroupId) {
      return `${path}?id=${effectiveShareGroupId}`;
    }
    return path;
  };

  // Function to clear guest share tokens (can be called from console: window.clearGuestShare())
  const clearGuestShare = () => {
    localStorage.removeItem('guestShareGroupId');
    localStorage.removeItem('guestShareGroupToken');
    setStoredShareGroupId(null);
    setStoredShareGroupToken(null);
    // Reload to clear URL parameters
    window.location.href = '/landing';
  };

  // Expose clear function globally for console access
  useEffect(() => {
    window.clearGuestShare = clearGuestShare;
    return () => {
      delete window.clearGuestShare;
    };
  }, []);

  // If no user is logged in, we show a minimal header with a Sign In button
  const nameToDisplay = userData?.name || (user ? user.email : "Guest");
  const userCredits = userData?.credits ?? 0;

  return (
    <header className="header-container">
      <div className="logo">
        <a href="/homepage">
          <img src="/logo.svg" alt="Chronocult" style={{  height: '24px' }} />
        </a>
        <span className="beta-label">Beta 0.23</span>
      </div>

      {/* Navigation Menu - Desktop */}
      {(!user?.isAnonymous || showGuestNav) && (
        <nav className="header-navigation header-desktop-only">
          <button 
            className="header-nav-btn"
            onClick={() => navigate(isGuest ? buildGuestPath('/runs') : '/homepage')}
          >
            Home
          </button>
          <span className="header-nav-divider">|</span>
          <button 
            className="header-nav-btn"
            onClick={() => navigate(isGuest ? buildGuestPath('/runs') : '/runs-list')}
          >
            Runs
          </button>
          {showGuestNav && (
            <>
              <span className="header-nav-divider">|</span>
              <button
                className="header-nav-btn"
                onClick={() => navigate(buildGuestPath('/guest-tracks'))}
              >
                Tracks
              </button>
            </>
          )}
          <span className="header-nav-divider">|</span>
          <button 
            className="header-nav-btn"
            onClick={() => navigate(isGuest ? buildGuestPath('/racers-list') : '/racers-list')}
          >
            Racers
          </button>
          <span className="header-nav-divider">|</span>
          <button 
            className="header-nav-btn"
            onClick={() => navigate(isGuest ? buildGuestPath('/groups') : '/groups')}
          >
            Groups
          </button>
          {!isGuest && user && !user.isAnonymous && (
            <>
              <span className="header-nav-divider">|</span>
              <button 
                className="header-nav-btn"
                onClick={() => navigate('/sharing-groups')}
              >
                Sharing Groups
              </button>
            </>
          )}
        </nav>
      )}

      {/* Desktop version */}
      {user && !user.isAnonymous ? (
        <div
          className="header-user-info header-desktop-only"
          style={{
            marginLeft: 'auto',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            marginRight: '10px',
            flexShrink: 0,
          }}
        >
          <button style={styles.buyCreditsBtn} onClick={handleBuyCredits}>
            Buy Credits
          </button>
          <button style={styles.logoutBtn} onClick={handleSignOut}>
            Sign Out
          </button>
          <div style={{ display: 'flex', alignItems: 'center', backgroundColor: '#222', borderRadius: '5px', marginLeft: '10px', padding: '6px 12px' }} onClick={handleBuyCredits}>
            <span>{userCredits} ⚡</span>
          </div>
        </div>
      ) : (
        <div
          className="header-user-info header-desktop-only"
          style={{
            marginLeft: 'auto',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            marginRight: '10px',
            gap: '10px',
            flexShrink: 0,
          }}
        >
          <span style={{ color: '#fff' }}>Hello, Guest</span>
          {(effectiveShareGroupId || effectiveShareGroupToken) && (
            <button 
              style={{ ...styles.buyCreditsBtn, backgroundColor: '#666', fontSize: '0.85em' }} 
              onClick={clearGuestShare}
              title="Clear share session and return to landing page"
            >
              Clear Share
            </button>
          )}
          <button style={styles.buyCreditsBtn} onClick={handleSignIn}>
            Sign In
          </button>
        </div>
      )}

      {/* Mobile version */}
      <div className="header-mobile-only" style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center' }}>
        <button 
          onClick={() => setMobileMenuOpen(true)} 
          style={{ background: 'transparent', border: 'none', fontSize: '1.8em', cursor: 'pointer', color: '#ccc' }}
        >
          <FaBars />
        </button>
      </div>

      {/* Mobile menu overlay */}
      {isMobileMenuOpen && (
        <div className="mobile-menu-overlay">
          <div className="mobile-menu">
            <button 
              onClick={() => setMobileMenuOpen(false)}
              style={{
                background: 'transparent',
                border: 'none',
                fontSize: '1.5em',
                color: '#fff',
                position: 'absolute',
                top: '10px',
                right: '10px',
                cursor: 'pointer'
              }}
            >
              <FaTimes />
            </button>
            <div style={{ padding: '20px', color: '#fff', display: 'flex', flexDirection: 'column', gap: '15px' }}>
              <span style={{ display: 'block' }}>
                Hello, <strong>{nameToDisplay}</strong>
              </span>
              {user && !user.isAnonymous ? (
                <>
                  <button 
                    className="mobile-nav-btn"
                    onClick={() => { navigate('/homepage'); setMobileMenuOpen(false); }}
                  >
                    Home
                  </button>
                  <button 
                    className="mobile-nav-btn"
                    onClick={() => { navigate('/runs-list'); setMobileMenuOpen(false); }}
                  >
                    Runs
                  </button>
                  <button 
                    className="mobile-nav-btn"
                    onClick={() => { navigate('/tracks'); setMobileMenuOpen(false); }}
                  >
                    Tracks
                  </button>
                  <button 
                    className="mobile-nav-btn"
                    onClick={() => { navigate('/racers-list'); setMobileMenuOpen(false); }}
                  >
                    Racers
                  </button>
                  <button 
                    className="mobile-nav-btn"
                    onClick={() => { navigate('/groups'); setMobileMenuOpen(false); }}
                  >
                    Groups
                  </button>
                  <button 
                    className="mobile-nav-btn"
                    onClick={() => { navigate('/sharing-groups'); setMobileMenuOpen(false); }}
                  >
                    Sharing Groups
                  </button>
                  <span>
                    Credits: {userCredits}
                    <Tooltip textKey="credits" />
                  </span>
                  <button style={styles.buyCreditsBtn} onClick={handleBuyCredits}>
                    Buy Credits
                  </button>
                  <button style={styles.logoutBtn} onClick={handleSignOut}>
                    Sign Out
                  </button>
                </>
              ) : (
                <>
                  {showGuestNav && (
                    <>
                      <button 
                        className="mobile-nav-btn"
                        onClick={() => { navigate(buildGuestPath('/runs')); setMobileMenuOpen(false); }}
                      >
                        Runs
                      </button>
                      <button 
                        className="mobile-nav-btn"
                        onClick={() => { navigate(buildGuestPath('/guest-tracks')); setMobileMenuOpen(false); }}
                      >
                        Tracks
                      </button>
                      <button 
                        className="mobile-nav-btn"
                        onClick={() => { navigate(buildGuestPath('/racers-list')); setMobileMenuOpen(false); }}
                      >
                        Racers
                      </button>
                      <button 
                        className="mobile-nav-btn"
                        onClick={() => { navigate(buildGuestPath('/groups')); setMobileMenuOpen(false); }}
                      >
                        Groups
                      </button>
                    </>
                  )}
                  {(effectiveShareGroupId || effectiveShareGroupToken) && (
                    <button 
                      style={{ ...styles.buyCreditsBtn, backgroundColor: '#666', fontSize: '0.9em', marginTop: '10px' }} 
                      onClick={() => { clearGuestShare(); setMobileMenuOpen(false); }}
                      title="Clear share session and return to landing page"
                    >
                      Clear Share
                    </button>
                  )}
                  <button style={styles.buyCreditsBtn} onClick={handleSignIn}>
                    Sign In
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}

const styles = {
  buyCreditsBtn: {
    padding: '6px 12px',
    backgroundColor: '#13A1A8',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    marginRight: '10px',
  },
  logoutBtn: {
    padding: '6px 12px',
    backgroundColor: '#d9534f',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
  },
  dropdownBtn: {
    padding: '6px 12px',
    backgroundColor: 'transparent',
    color: '#fff',
    border: '0px solid #fff',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '0.9em'
  },
  dropdownContent: {
    with: '300px',
    display: 'none',
    position: 'absolute',
    top: '100%',
    left: 0,
    backgroundColor: '#222',
    color: '#000',
    minWidth: '160px',
    boxShadow: '0 8px 16px rgba(0,0,0,0.2)',
    zIndex: 9000,
    padding: '8px 0',
  },
  dropdownLink: {
    display: 'block',
    padding: '8px 12px',
    textDecoration: 'none',
    color: '#ddd',
    fontSize: '0.9em'
  }
};

export default Header;